import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { SharedModule } from '../../shared/shared.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LabelService } from '../../services/label.service';
import {
  NavigationItem,
  navigationItemsWithPatient,
} from '../../models/navigation-item-model';
import { classes, pages } from '../../shared/labels';
import {
  LabelAndDescription,
  PatientDocument,
  PatientEssentialInfo,
} from '../../models/patient-model';
import { PatientService } from '../../services/patient.service';
import { ErrorPanelService } from '../../services/error-panel.service';
import { ActivatedRoute } from '@angular/router';
import { SidebarService } from '../../services/sidebar.service';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationPanelComponent } from '../../shared/confirmation-panel/confirmation-panel.component';

@Component({
  selector: 'app-patient-documents-page',
  standalone: true,
  imports: [
    SharedModule,
    CommonModule,
    FormsModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './patient-documents-page.component.html',
  styleUrl: './patient-documents-page.component.scss',
})
export class PatientDocumentsPageComponent implements OnInit {
  navigationItems: NavigationItem[] = [];
  patientName: string = '';
  patientSurname: string = '';
  patientNameAndSurname: string = '';
  isPatientActive: boolean = false;
  hasModifyButtonBeenClicked: boolean = false;
  rowIconMap: { [binaryValue: number]: string } = {};
  rowIcon: string = 'download';

  documents: PatientDocument[] = [];
  selectedDocument: PatientDocument = {
    id: '',
    patient_id: '',
    title: '',
    file_content_type: '',
    date: '',
  };

  patientId = '';
  showAddDocumentPanel: boolean = false;
  inputBoxesForNewDocument: LabelAndDescription[] = [];
  inputBoxesForModifyDocument: LabelAndDescription[] = [];
  showDeleteConfirmationPanel: boolean = false;
  showModifyDocumentPanel: boolean = false;

  displayedColumns: string[] = ['date', 'title', 'format', 'actions'];

  isMobile = window.innerWidth < 768;
  isLoading: boolean = true;

  constructor(
    public labelService: LabelService,
    private patientService: PatientService,
    public errorPanelService: ErrorPanelService,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private sidebarService: SidebarService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.patientService
        .getPatientEssentialInfo(params['patientId'])
        .subscribe({
          next: (data: PatientEssentialInfo) => {
            this.patientId = data.id;
            this.patientName = data.first_name;
            this.patientSurname = data.last_name;
            this.isPatientActive = data.is_active;
            this.patientNameAndSurname = `${this.patientName} ${this.patientSurname}`;
            this.navigationItems = this.sidebarService.updateNavigationItems(
              navigationItemsWithPatient,
              this.patientName,
              this.patientSurname,
              this.patientId,
              pages.documents
            );
            this.getDocuments();
          },
          error: (err) => {
            console.error('Error getting patient essential info:', err);
            this.errorPanelService.showErrorSnackBar(
              'Errore nel recuperare i dati del paziente'
            );
          },
        });
    });

    this.rowIconMap[0] = 'download';
    this.rowIconMap[1] = 'edit';
  }

  getDocuments() {
    this.isLoading = true;
    this.documents = [];

    this.patientService.getDocuments(this.patientId).subscribe({
      next: (response) => {
        response.map((doc: PatientDocument) => {
          this.documents.push(doc);
        });
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Errore nel recuperare i documenti', error);
        this.isLoading = false;
        this.errorPanelService.showErrorSnackBar(
          'Errore nel recuperare i documenti'
        );
      },
    });
  }

  onModifyButtonClick() {
    this.hasModifyButtonBeenClicked = !this.hasModifyButtonBeenClicked;
    this.rowIcon = this.rowIconMap[this.hasModifyButtonBeenClicked ? 1 : 0];
  }

  toggleAddDocumentPanel() {
    this.showAddDocumentPanel = !this.showAddDocumentPanel;

    if (this.showAddDocumentPanel) {
      var obj: LabelAndDescription[] = [
        { label: 'title', description: '' },
        { label: 'date', description: '' },
      ];
      this.inputBoxesForNewDocument = obj;
    }
  }

  confirmNewDocument(event: any) {
    const documentData = {
      patient_id: this.patientId,
      title:
        event.inputs.find((f: any) => f.label === 'title')?.description || '',
      date:
        event.inputs.find((f: any) => f.label === 'date')?.description || '',
    };

    this.patientService
      .createNewDocument(JSON.stringify(documentData), event.file)
      .subscribe({
        next: (response) => {
          this.toggleAddDocumentPanel();
          this.getDocuments();
        },
        error: (error) => {
          console.error('Errore nel caricare il documento', error);
          this.errorPanelService.showErrorSnackBar(
            'Errore nel caricare il documento'
          );
        },
      });
  }

  onViewDocument(document: PatientDocument) {
    this.patientService.getDocumentFile(document.id).subscribe({
      next: (response) => {
        // Crea un URL dal Blob
        const blob = new Blob([response], { type: document.file_content_type });
        const url = window.URL.createObjectURL(blob);

        // Apri il documento in una nuova scheda
        const newTab = window.open(url, '_blank');
        if (!newTab) {
          window.alert(
            'Impossibile aprire il documento in una nuova scheda. Assicurati che il popup blocker sia disabilitato.'
          );
        }

        // Ritarda la revoca dell'URL per dare tempo alla nuova scheda di caricare il documento
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000); // Ritardo di 1000ms (1 secondo)
      },
      error: (error) => {
        console.error('Errore durante il download del documento:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel download del documento'
        );
      },
    });
  }

  onDownloadDocument(patientDocument: PatientDocument) {
    this.patientService.getDocumentFile(patientDocument.id).subscribe({
      next: (response) => {
        // Crea un URL dal Blob
        const blob = new Blob([response], {
          type: patientDocument.file_content_type,
        });
        const url = window.URL.createObjectURL(blob);

        // Crea un elemento <a> per scaricare il file
        const a = document.createElement('a');
        a.href = url;
        a.download = patientDocument.title;
        document.body.appendChild(a);
        a.click();

        // Pulisci l'URL e rimuovi l'elemento <a> dopo il download
        window.URL.revokeObjectURL(url);
        a.remove();
      },
      error: (error) => {
        console.error('Errore durante il download del documento:', error);
        this.errorPanelService.showErrorSnackBar(
          'Errore nel download del documento'
        );
      },
    });
  }

  toggleModifyPanel(document?: PatientDocument) {
    this.showModifyDocumentPanel = !this.showModifyDocumentPanel;
    if (document) {
      this.selectedDocument = document;
      var obj: LabelAndDescription[] = [
        { label: 'title', description: document.title },
        { label: 'date', description: document.date },
      ];
      this.inputBoxesForModifyDocument = obj;
    } else {
      this.selectedDocument = {
        id: '',
        patient_id: '',
        title: '',
        file_content_type: '',
        date: '',
      };
      this.inputBoxesForModifyDocument = [];
    }
  }

  modifyDocument(event: LabelAndDescription[]) {
    // transform from array to a json
    var mergedObject = event.reduce<{ [key: string]: any }>((acc, current) => {
      acc[current.label] = current.description;
      return acc;
    }, {});

    this.patientService
      .putDocument(this.selectedDocument.id, mergedObject)
      .subscribe({
        next: (response) => {
          this.toggleModifyPanel();
          this.getDocuments();
        },
        error: (error) => {
          console.error("Errore nell'aggiornare il documento", error);
          this.toggleModifyPanel();
          this.errorPanelService.showErrorSnackBar(
            "Errore nell'aggiornare il documento"
          );
        },
      });
  }

  deleteDocument(document: PatientDocument) {
    const dialogRef = this.dialog.open(ConfirmationPanelComponent, {
      data: {
        title: 'Sei sicuro di voler eliminare questo documento?',
        infoText: "L'operazione è irreversibile.",
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.patientService.deleteDocument(document.id).subscribe({
          next: (response) => {
            this.getDocuments();
          },
          error: (error) => {
            console.error('Errore con la eliminazione del documento', error);
            this.errorPanelService.showErrorSnackBar(
              "Errore nell'eliminazione del documento"
            );
          },
        });
      }
    });
  }

  handleMenuAction(action: string, document: PatientDocument) {
    switch (action) {
      case 'preview':
        if (document.file_content_type === 'application/pdf') {
          this.onViewDocument(document);
        }
        break;
      case 'download':
        this.onDownloadDocument(document);
        break;
      case 'edit':
        this.toggleModifyPanel(document);
        break;
      case 'delete':
        this.deleteDocument(document);
        break;
    }
  }

  displayFileType(fileType: string) {
    switch (fileType) {
      case 'application/pdf':
        return 'PDF';
      case 'application/msword':
        return 'DOC';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'DOCX';
      case 'application/x-iwork-pages-sffpages':
        return 'PAGES';
      default:
        return fileType;
    }
  }
}
